import { createFileRoute, Link } from "@tanstack/react-router";
import { ButtonLink } from "@/components/ui-ext/Button";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { Picture } from "@/components/media/Picture";
import { ldScripts, faqLd, serviceLd, breadcrumbLd, pageMeta } from "@/lib/seo";
import { SEO_SERVICE_SEO, SEO_SERVICE_PILLARS, SEO_SERVICE_SECTIONS, SEO_SERVICE_FAQS } from "@/content/seo-service";

export const Route = createFileRoute("/services/seo")({
  head: () => ({
    ...pageMeta({ path: "/services/seo/", ...SEO_SERVICE_SEO }),
    scripts: ldScripts(
      serviceLd({ name: "Austin SEO Services", description: SEO_SERVICE_SEO.description, path: "/services/seo/" }),
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services/" }, { name: "Austin SEO Services", path: "/services/seo/" }]),
      faqLd(SEO_SERVICE_FAQS),
    ),
  }),
  component: SeoServicePage,
});

function SeoServicePage() {
  return (
    <div>
      <section className="container-page pt-8 pb-12 md:pb-16 border-b border-border">
        <Breadcrumbs items={[{ name: "Services", path: "/services/" }, { name: "Austin SEO Services", path: "/services/seo/" }]} />
        <div className="mt-8 max-w-4xl">
          <EyebrowLabel>Optimize Plus · Austin, Texas</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-tight text-balance">Austin SEO Services</h1>
          <p className="mt-5 font-display text-2xl md:text-3xl text-primary">Turn search visibility into qualified enquiries.</p>
          <p className="mt-5 max-w-3xl text-lg text-muted-foreground leading-relaxed">An Austin SEO agency combining technical SEO, local search, content and AI search optimization. Build a clearer path from the questions your customers ask to the services you deliver.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <ButtonLink to="/contact/" search={{ location: "", service: "seo" }} size="lg">Book a Strategy Call →</ButtonLink>
            <ButtonLink to="/case-studies/" variant="outline" size="lg">View Case Studies</ButtonLink>
          </div>
        </div>
        <Picture variantKey="austin" alt="Austin skyline, the home city of Optimize Plus" width={1600} height={720} priority sizes="(min-width: 1024px) 1100px, 100vw" className="mt-8 block overflow-hidden rounded-lg" imgClassName="w-full h-auto object-cover" />
      </section>

      {SEO_SERVICE_SECTIONS.map((section, index) => (
        <section key={section.id} id={section.id} className="container-page py-12 md:py-16 border-b border-border">
          <div className="max-w-3xl">
            <EyebrowLabel>{index === 0 ? "Our approach" : index === 1 ? "Delivery" : "Measurement"}</EyebrowLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">{section.title}</h2>
            <div className="mt-6 space-y-5">
              {section.paragraphs.map((paragraph) => <p key={paragraph} className="text-base md:text-lg text-muted-foreground leading-relaxed">{paragraph}</p>)}
            </div>
          </div>
          {index === 0 && (
            <div id="disciplines" className="mt-10 grid gap-x-10 gap-y-8 md:grid-cols-2">
              {SEO_SERVICE_PILLARS.map((pillar) => (
                <div key={pillar.slug} className="border-t border-border pt-5 min-w-0">
                  <h3 className="font-display text-xl md:text-2xl leading-tight">{pillar.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{pillar.body}</p>
                  <Link to="/services/$slug/" params={{ slug: pillar.slug }} className="mt-4 inline-block text-primary">Explore this service →</Link>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}

      <section className="container-page py-12 md:py-16 border-b border-border">
        <div className="max-w-3xl">
          <EyebrowLabel>Scope & investment</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl">The right programme for your next stage.</h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">A single-location business, a regional operator and a national B2B team need different scopes. Review our current programme options, then discuss the services, markets and implementation resources that matter to your business.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <ButtonLink to="/pricing/" variant="outline">View Pricing →</ButtonLink>
            <ButtonLink to="/case-studies/" variant="outline">View Case Studies</ButtonLink>
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed">Explore our <Link to="/industries/" className="text-primary">industry playbooks</Link> or <Link to="/locations/" className="text-primary">Texas service areas</Link> for context on your market.</p>
        </div>
      </section>

      <section id="faq" className="container-page py-12 md:py-16 border-b border-border">
        <h2 className="font-display text-3xl md:text-4xl">Austin SEO: your questions answered.</h2>
        <div className="mt-6 max-w-3xl divide-y divide-border">
          {SEO_SERVICE_FAQS.map((faq) => <details key={faq.q} className="py-5 group"><summary className="cursor-pointer font-medium text-lg">{faq.q}</summary><p className="mt-4 text-muted-foreground leading-relaxed">{faq.a}</p></details>)}
        </div>
      </section>
      <section className="container-page py-12 md:py-16">
        <h2 className="font-display text-3xl md:text-4xl">Make your next search decision with clarity.</h2>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Discuss your website, your priority services and the kind of enquiries you want to attract.</p>
        <ButtonLink to="/contact/" search={{ location: "", service: "seo" }} className="mt-6" size="lg">Book a Strategy Call →</ButtonLink>
      </section>
    </div>
  );
}
